<script setup lang="ts">
import type { IconName } from "../Icon/icons";
import type { IconTileTone } from "../IconTile/VIconTile.types";
import type { AlertBodyProps, AlertContainerProps } from "./VAlert.types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import VIcon from "../Icon/VIcon.vue";
import VIconTile from "../IconTile/VIconTile.vue";
import {
  alertBodyCva,
  alertCloseButtonCva,
  alertContainerCva,
  alertContentCva,
  alertIconCva,
} from "./VAlert.cva";

interface Props {
  closeable?: boolean
  variant?: AlertContainerProps["variant"]
  iconLeft?: IconName | null
  size?: AlertBodyProps["size"]
  background?: boolean
  /**
   * Draws the icon in a 32px tinted tile (VIconTile) instead of bare, for a banner that must stand out, such as the
   * emergency notice of a guest page.
   */
  iconTile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  closeable: false,
  variant: "danger",
  iconLeft: null,
  size: "medium",
  background: true,
  iconTile: false,
});

const emit = defineEmits<{
  (e: "close"): void
}>();

const slots = defineSlots<{
  default?: () => unknown
  title?: () => unknown
  icon?: () => unknown
  /** Buttons or links on the right of the text; they move under the text when the alert is narrow. */
  actions?: () => unknown
}>();

const hasActions = computed(() => Boolean(slots.actions));

const { t } = useI18n();

const computedIconLeft = computed<IconName | null>(() => {
  if (props.iconLeft === null) {
    switch (props.variant) {
      case "warning":
        return "triangle-alert";
      case "danger":
        return "circle-alert";
      case "info":
        return "info";
      case "success":
        return "circle-check";
      default:
        return null;
    }
  }
  return props.iconLeft;
});

const containerClasses = computed(() => {
  return alertContainerCva({
    variant: props.variant,
    background: props.background,
    align: hasActions.value ? "center" : "start",
  });
});

const iconClasses = computed(() => {
  return alertIconCva({
    variant: props.variant,
    background: props.background,
  });
});

const contentClasses = computed(() => {
  return alertContentCva();
});

const bodyClasses = computed(() => {
  return alertBodyCva({
    size: props.size,
  });
});

const closeButtonClasses = computed(() => {
  return [
    alertCloseButtonCva({
      variant: props.variant,
      background: props.background,
    }),
    "rounded-xs focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-current focus-visible:opacity-100",
  ];
});

// The tile sits on the tinted alert, so a tinted background takes the stronger tile of its colour.
const TILE_TONE: Record<NonNullable<AlertContainerProps["variant"]>, IconTileTone> = {
  "primary": "primary-strong",
  "secondary": "neutral",
  "info": "info",
  "success": "success",
  "danger": "danger-strong",
  "warning": "caution",
  "white": "neutral",
  "gray": "neutral",
  "text-color": "neutral",
};
const tileTone = computed<IconTileTone>(() => TILE_TONE[props.variant ?? "danger"]);
// Urgent variants interrupt assistive tech; the rest are announced politely.
const role = computed(() => (props.variant === "danger" || props.variant === "warning" ? "alert" : "status"));
const closeLabel = computed(() => t("base.close"));
</script>

<template>
  <div
    :class="[containerClasses, { '@container': hasActions }]"
    :role="role"
  >
    <div
      class="flex min-w-0 flex-1 gap-3"
      :class="hasActions ? 'flex-col @[20rem]:flex-row @[20rem]:items-center' : undefined"
    >
      <div class="flex gap-2.5 flex-1 min-w-0" :class="{ 'items-center gap-3': iconTile }">
        <VIconTile
          v-if="iconTile && computedIconLeft && !$slots.icon"
          :icon="computedIconLeft"
          :tone="tileTone"
          size="sm"
          data-testid="alert-icon-tile"
        />
        <div v-else-if="computedIconLeft || $slots.icon" :class="iconClasses">
          <slot name="icon">
            <VIcon v-if="computedIconLeft" :name="computedIconLeft" aria-hidden="true" />
          </slot>
        </div>
        <div :class="contentClasses">
          <div v-if="$slots.title" class="font-medium text-sm leading-snug">
            <slot name="title" />
          </div>
          <div :class="bodyClasses">
            <slot />
          </div>
        </div>
      </div>
      <div
        v-if="hasActions"
        class="flex flex-wrap items-center gap-2 *:flex-1 @[20rem]:shrink-0 @[20rem]:*:flex-none"
        data-testid="alert-actions"
      >
        <slot name="actions" />
      </div>
    </div>
    <button
      v-if="closeable"
      type="button"
      :class="closeButtonClasses"
      :aria-label="closeLabel"
      @click="emit('close')"
    >
      <VIcon name="x" size="1.25em" aria-hidden="true" />
    </button>
  </div>
</template>
